// The opponent's brief, on screen, while it is being used.
//
// A developer view. The planning layer is the only part of this game with no
// visible output — a belt shows its cargo, a foundry shows its recipe, a march
// draws itself on the board — and the brief that now decides every turn lives
// for one turn inside a worker and is seen by nobody. Three of this project's
// longer debugging sessions were numbers nobody was looking at.
//
// **Recomputed here rather than carried back from the worker.** The board is
// frozen while the opponent thinks, and `materialBrief` is pure — so the same
// state gives the same brief, and the alternative is a second channel through
// the worker message that could drift from the thing it reports on.

import { materialBrief } from "src/engine/ai/flows";
import type { GameState } from "src/engine/rules/commands";
import { planningRows } from "./planningView";
import { Panel, SectionTitle, Text, styled, useEdgeInsets } from "./kit";
import { useDevTools } from "./useDevTools";

/**
 * Which ink a row takes: a plan, a fresh proposal, and everything else.
 *
 * Four kinds against the language's five tones, so the split is what the row
 * *is* rather than a colour per kind — the running total, the plan being
 * followed, a proposal that has not won yet, and an aside.
 */
const TONE = {
  summary: "brand",
  plan: "cream",
  proposal: "ink",
  note: "dim"
} as const;

/**
 * The drawer. Bottom-left, above whatever the chrome covers down there.
 *
 * `z.dev` is the developer rung — over every part of the game, under nothing
 * but a hover card. It is a reading and never a control, so it takes no
 * pointer: a panel over the board that ate a click meant for a square is the
 * failure every overlay in this project is written against.
 */
const Drawer = styled(Panel)(({ theme }) => ({
  position: "fixed",
  left: 16,
  zIndex: theme.kit.z.dev,
  width: 340,
  overflowY: "auto",
  padding: theme.kit.space.sm,
  pointerEvents: "none"
}));

/**
 * One line of the brief.
 *
 * `nowrap` on purpose: these are columns of numbers read at a glance while a
 * turn is being planned, and a row that wraps moves every row under it.
 */
const Row = styled(Text)({
  display: "block",
  fontSize: 11,
  lineHeight: 1.5,
  whiteSpace: "nowrap"
});

export function PlanningDrawer({ state }: { state: GameState }) {
  // Asked before the early return, or the hook count changes with the answer —
  // which is why the registry is read here too rather than beside the style.
  const devTools = useDevTools();
  const insets = useEdgeInsets();
  if (!devTools) return null;

  // **Off the registry, not off a fixed dock height** (PLAN-UI §28.5, U8).
  // `insets.bottom` is whatever the chrome covers at the bottom edge — the
  // hand, while cards are drawn, and nothing when they are not — and 64 clears
  // the dev-menu button that sits in that corner (`DevMenu.tsx`, `bottom: 16`).
  const above = insets.bottom + 64;

  const rows = planningRows(state, materialBrief(state, state.position.turn));

  return (
    <Drawer
      data-testid="planning-drawer"
      // In `style` rather than `sx`: it moves with the hand, and emotion mints
      // a class per distinct value.
      style={{ bottom: above, maxHeight: `calc(100vh - ${above + 48}px)` }}
    >
      <SectionTitle sx={{ display: "block", mb: 0.5 }}>What they are planning</SectionTitle>
      {rows.map((row, i) => (
        <Row
          key={i}
          tone={TONE[row.kind]}
          // The two machine kinds are columns of figures, so they take the
          // display face and its tabular numerals; a summary and an aside are
          // sentences and take the body.
          role={row.kind === "summary" || row.kind === "note" ? "body" : "display"}
        >
          {row.text}
        </Row>
      ))}
    </Drawer>
  );
}
